import React, { useEffect, useRef, useState, useContext } from "react";
import { Box, Button } from "@mui/material";
import { getTasksDataApi } from "../../api/schedule";
import { getContractorsApi } from "../../api/member.js";
import { getElementsDataApi, getMainModelDataApi, getBackgroundModelUrlApi } from "../../api/model";
import { getElementStatus, calculateGanttDateRange } from "../../utils/schedule.js";
import { useParams } from "react-router-dom";
import { useUser } from "../../hook/useUser.js";
import { useProject } from "../../hook/useProject.js";
import { NotificationContext } from "../../component/popup/NotificationProvider";
import dayjs from "dayjs";
import SideBySideViewer from "../../component/bim/SideBySideViewer.js";
import SelectComponent from "../../component/SelectComponent.js";
import ManageSchedulePopup from "./components/ManageSchedulePopup.js";
import ScheduleGantt from "./components/ScheduleGantt.js";
import DateConditions from "../../component/DateConditions.js";
import FullScreenLoading from "../../component/FullScreenLoading.js";

const buttonStyle = {
  pr: 1.5,
  pl: 1,
  py: 0,
  lineHeight: "35px",
  borderRadius: "20px",
};

const SchedulePage = () => {
  const { projectId } = useParams();
  const { showError } = useContext(NotificationContext);
  const projectDetails = useProject(projectId);
  const { user } = useUser(projectId);
  const sideBysideViewer = useRef();

  const [elementIds, setElementIds] = useState({
    planProgress: [],
    actualProgress: [],
    delayed: [],
    planComplete: [],
    actualComplete: [],
    showFilter: [],
    white: [],
    all: [],
    highlight: [],
  });

  const [state, setState] = useState({
    contractorOptions: [],
    contractorTaskValue: [],
    contractorElementValue: [],
    workSetOptions: [],
    backgroundOptions: [],
    worksetValue: [],
    backgroundValue: [],
    backgroundModels: [],
    taskTableData: [],
    filteredTaskData: [],
    startDate: dayjs(),
    endDate: dayjs(),
    currentDate: dayjs(),
    selectedRowId: "",
    showPopup: false,
    modelURL: [],
    elementsData: [],
    isLoading: true,
  });

  const selectComponents = [
    {
      Component: SelectComponent,
      options: state.workSetOptions,
      value: state.worksetValue,
      isMultiSelect: true,
      onChange: (value) => {
        setState((prevState) => ({
          ...prevState,
          worksetValue: value,
          modelURL: state.modelURL.map((mainModel) => {
            return {
              ...mainModel,
              visible: value.includes(mainModel.workset),
            };
          }),
        }));
      },
      selectLabel: "Workset",
      placeholder: "select an item...",
    },
    {
      Component: SelectComponent,
      options: state.contractorOptions,
      value: state.contractorElementValue,
      isMultiSelect: true,
      onChange: (value) => {
        setState((prevState) => ({
          ...prevState,
          contractorElementValue: value,
        }));
      },
      selectLabel: "Contractor",
      placeholder: "select an item...",
    },
    {
      Component: SelectComponent,
      options: state.backgroundOptions,
      value: state.backgroundValue,
      isMultiSelect: true,
      required: false,
      onChange: (value) => {
        setState((prevState) => ({
          ...prevState,
          backgroundValue: value,
          backgroundModels: state.backgroundModels.map((backgroundModel) => {
            return {
              ...backgroundModel,
              visible: value.includes(backgroundModel.fileURL),
            };
          }),
        }));
      },
      selectLabel: "Background",
      placeholder: "select an item...",
    },
  ];

  const handleSingleSelectChange = (value) => {
    setState((prevState) => ({
      ...prevState,
      contractorTaskValue: value,
    }));
  };

  const handleStartDateChange = (newStartDate) => {
    setState((prevState) => ({
      ...prevState,
      startDate: newStartDate,
    }));
    if (newStartDate && (state.currentDate === null || newStartDate.isAfter(state.currentDate))) {
      setState((prevState) => ({
        ...prevState,
        currentDate: newStartDate,
      }));
    }
  };

  const handleEndDateChange = (newEndDate) => {
    setState((prevState) => ({
      ...prevState,
      endDate: newEndDate,
    }));
    if (newEndDate && (state.currentDate === null || newEndDate.isBefore(state.currentDate))) {
      setState((prevState) => ({
        ...prevState,
        currentDate: newEndDate,
      }));
    }
  };

  const handleCurrentDateChange = (newCurrentDate) => {
    if (newCurrentDate && state.startDate && newCurrentDate.isBefore(state.startDate)) {
      setState((prevState) => ({
        ...prevState,
        currentDate: state.startDate,
      }));
    } else if (newCurrentDate && state.endDate && newCurrentDate.isAfter(state.endDate)) {
      setState((prevState) => ({
        ...prevState,
        currentDate: state.endDate,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        currentDate: newCurrentDate,
      }));
    }
  };

  const handlePrevDay = () => {
    setState((prevState) => ({
      ...prevState,
      currentDate: prevState.currentDate ? prevState.currentDate.subtract(1, "day") : dayjs().subtract(1, "day"),
    }));
  };
  const handleNextDay = () => {
    setState((prevState) => ({
      ...prevState,
      currentDate: prevState.currentDate ? prevState.currentDate.add(1, "day") : dayjs().add(1, "day"),
    }));
  };

  const handleRowClick = (row) => {
    // deselect
    if (state.selectedRowId === row.taskId) {
      setState((prevState) => ({
        ...prevState,
        selectedRowId: undefined,
        // currentDate: dayjs(row.planStart),
      }));
      setElementIds((prevState) => ({
        ...prevState,
        highlight: [],
      }));
    } else {
      const elements = state.elementsData.filter((elem) => elem.taskId === row.taskId);
      const bimElementIds = elements.map((elem) => elem.bimElementId);
      setState((prevState) => ({
        ...prevState,
        selectedRowId: row.taskId,
        // currentDate: dayjs(row.planStart),
      }));
      setElementIds((prevState) => ({
        ...prevState,
        highlight: bimElementIds,
      }));
    }
  };

  const handleClosePopup = () => {
    setState((prevState) => ({
      ...prevState,
      showPopup: false,
    }));
  };

  //get main model url and workset
  const getMainModel = async () => {
    const result = await getMainModelDataApi(projectId);
    const data = result?.data;

    if (data && Array.isArray(data)) {
      const mainModels = data.map((d) => {
        return {
          ...d,
          fileURL: d.fileURL,
          visible: true,
        };
      });

      setState((prevState) => ({
        ...prevState,
        modelURL: mainModels,
      }));

      const uniqueWorksets = new Set(data.map((item) => item.workset));
      const workSetOptionsArray = Array.from(uniqueWorksets).map((workset) => ({
        value: workset,
        label: workset,
      }));
      setState((prevState) => ({
        ...prevState,
        workSetOptions: workSetOptionsArray,
      }));

      if (uniqueWorksets.size > 0) {
        setState((prevState) => ({
          ...prevState,
          worksetValue: Array.from(uniqueWorksets),
        }));
      }
      await getElementsData();
    }
  };

  //get background model url&filter
  const getBackgroundModelUrl = async () => {
    const result = await getBackgroundModelUrlApi(projectId);
    const data = result?.data;
    if (data && Array.isArray(data)) {
      const backgroundModels = data.map((d) => {
        return {
          ...d,
          fileURL: d.fileURL,
          visible: false,
        };
      });
      const backgroundArray = backgroundModels.map((item) => ({
        value: item.fileURL,
        label: item.filename,
      }));
      setState((prevState) => ({
        ...prevState,
        backgroundOptions: backgroundArray,
        backgroundModels: backgroundModels,
        // backgroundValue: backgroundArray.map((b) => b.value),
        backgroundValue: [],
      }));
    }
  };

  const filterTaskByContractors = () => {
    if (state.contractorTaskValue?.length === 0 || state.contractorTaskValue.length === state.contractorOptions.length) {
      setState((prevState) => ({
        ...prevState,
        filteredTaskData: state.taskTableData,
      }));
    } else {
      const normalizedSelected = state.contractorTaskValue.map((c) => c.toLowerCase().trim());

      const filtered = state.taskTableData.filter((task) => {
        const contractor = (task.contractor ?? "").toLowerCase().trim();
        return normalizedSelected.some((selected) => contractor.includes(selected));
      });

      setState((prevState) => ({
        ...prevState,
        filteredTaskData: filtered,
      }));
    }
  };

  const getTaskData = async () => {
    const result = await getTasksDataApi(projectId);
    const data = result?.data;
    if (!data || !Array.isArray(data)) {
      showError("Invalid or empty task data received.");
      return;
    }
    const { startDate, endDate } = calculateGanttDateRange(data, projectDetails);
    setState((prevState) => ({
      ...prevState,
      startDate,
      endDate,
      taskTableData: data,
      isLoading: false,
    }));
  };

  const colorRulesData = (data) => {
    const elementStatus = getElementStatus(data, state.currentDate);
    let allIds = data?.map((item) => item?.bimElementId);
    setElementIds((prevState) => ({
      ...prevState,
      ...elementStatus,
      all: allIds,
      // filter contractor
      hideElementIds:
        state.contractorElementValue.length === 0 || state.contractorElementValue.length === state.contractorOptions.length
          ? []
          : data.filter((item) => !state.contractorElementValue.includes(item.contractor)).map((item) => item?.bimElementId),
    }));
  };

  const getElementsData = async () => {
    const result = await getElementsDataApi(projectId);
    const data = result?.data;
    setState((prevState) => ({
      ...prevState,
      elementsData: data,
    }));
    colorRulesData(data);
  };

  //manage schedule upload new version
  const handleFileUploadSuccess = () => {
    getMainModel();
    getBackgroundModelUrl();
    getTaskData();
  };

  const getContractorsData = async () => {
    try {
      const result = await getContractorsApi(projectId);
      const data = result?.data;
      if (data) {
        const conTractors = new Set(data?.map((item) => item.name));
        const options = Array.from(conTractors).map((item) => ({
          label: item,
          value: item,
        }));
        setState((prevState) => ({
          ...prevState,
          contractorOptions: options,
        }));
        if (Array.from(conTractors).length > 0) {
          setState((prevState) => ({
            ...prevState,
            contractorTaskValue: Array.from(conTractors),
            contractorElementValue: Array.from(conTractors),
          }));
        }
      }
    } catch {
      showError("Received contractor data occurred error.");
    }
  };

  useEffect(() => {
    getMainModel();
    getBackgroundModelUrl();
    // getTaskData();
  }, []);

  useEffect(() => {
    if (!projectDetails) return;
    getContractorsData();
    getTaskData();
  }, [projectDetails]);

  useEffect(() => {
    if (state.worksetValue.length > 0 && state.elementsData.length > 0) {
      colorRulesData(state.elementsData);
    }
  }, [state.worksetValue, state.elementsData, state.currentDate, state.contractorElementValue]);

  useEffect(() => {
    if (!state.taskTableData) return;
    filterTaskByContractors();
  }, [state.taskTableData, state.contractorTaskValue]);

  return (
    <>
      <Box display="flex" width="100%" height="100%" flexDirection="column">
        <Box width="100%" height="35vh">
          {state.modelURL && (
            <SideBySideViewer
              ref={sideBysideViewer}
              modelURL={state.modelURL}
              backgroundModels={state.backgroundModels}
              leftTitle={"Plan Model View"}
              rightTitle={"Actual Model View"}
              elementIds={elementIds}
              selectComponents={selectComponents}
            ></SideBySideViewer>
          )}
        </Box>
        <Box display="flex" alignItems="center" py={1.5} gap={1}>
          <Button
            onClick={() =>
              setState((prevState) => ({
                ...prevState,
                showPopup: true,
              }))
            }
            variant="contained"
            color="secondary"
            sx={{ ...buttonStyle }}
            startIcon={<img src="assets/buttons/set.svg" alt="Manage Schedule" />}
          >
            Manage Schedule
          </Button>
          <DateConditions
            startDate={state.startDate}
            currentDate={state.currentDate}
            endDate={state.endDate}
            handleStartDateChange={handleStartDateChange}
            handleCurrentDateChange={handleCurrentDateChange}
            handleEndDateChange={handleEndDateChange}
            handlePrevDay={handlePrevDay}
            handleNextDay={handleNextDay}
          ></DateConditions>
          {state.contractorOptions && state.contractorTaskValue && (
            <SelectComponent
              options={state.contractorOptions}
              value={state.contractorTaskValue}
              onChange={handleSingleSelectChange}
              selectLabel="Contractor Selection:"
              isMultiSelect
            />
          )}
        </Box>
        <Box width="100%" height="calc(100% - 60px - 35vh)">
          <ScheduleGantt
            tasks={state.filteredTaskData}
            startDate={state.startDate}
            endDate={state.endDate}
            currentDate={state.currentDate}
            setCurrentDate={(d) =>
              setState((prevState) => ({
                ...prevState,
                currentDate: d ? dayjs(d) : null,
              }))
            }
            handleRowClick={handleRowClick}
            selectedRowId={state.selectedRowId}
            showButton
          ></ScheduleGantt>
        </Box>
      </Box>
      <ManageSchedulePopup open={state.showPopup} onClose={handleClosePopup} onUploadSuccess={handleFileUploadSuccess} user={user} />
      <FullScreenLoading isLoading={state.isLoading} />
    </>
  );
};

export default SchedulePage;
